import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Grid, VStack } from '@chakra-ui/react'
import { LuCircle, LuDownload, LuSearch } from 'react-icons/lu'
import { PageHeader, Text, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../../theme'
import { plantaService } from '../../../api/services/planta.service'
import type {
  BalanceDeviceStatusResponse,
  BalanzaListItemResponse,
} from '../../../api/models/response'

const formatWeight = (v: number | null | undefined) =>
  v == null
    ? '0.000'
    : v.toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })

export default function BalanzaPage() {
  const colors = themeColors

  // Estado del dispositivo (mock por ahora)
  const [device, setDevice] = useState<BalanceDeviceStatusResponse | null>(null)

  // Selección por código de lote
  const [searchCode, setSearchCode] = useState('')
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lote, setLote] = useState<BalanzaListItemResponse | null>(null)

  // Entradas y captura
  const [grossInput, setGrossInput] = useState('')
  const [tareInput, setTareInput] = useState('')
  const [capturing, setCapturing] = useState<'Gross' | 'Tare' | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    plantaService.getBalanceDeviceStatus().then(setDevice).catch(() => setDevice(null))
  }, [])

  useEffect(() => {
    if (lote) {
      setGrossInput(lote.grossWeight != null ? lote.grossWeight.toString() : '')
      setTareInput(lote.tare != null ? lote.tare.toString() : '')
    } else {
      setGrossInput('')
      setTareInput('')
    }
  }, [lote])

  const handleBuscar = useCallback(async () => {
    if (!searchCode.trim()) return
    setLookupLoading(true)
    try {
      const r = await plantaService.getBalanzaList({ search: searchCode.trim(), pageSize: 1 })
      const item = r.items[0] ?? null
      if (!item) {
        toaster.error({ description: 'No se encontro un lote en planta con ese codigo.' })
      }
      setLote(item)
    } finally {
      setLookupLoading(false)
    }
  }, [searchCode])

  const handleCapturar = useCallback(
    async (type: 'Gross' | 'Tare') => {
      if (!device || device.status !== 'Connected') {
        toaster.error({ description: 'Dispositivo de balanza desconectado.' })
        return
      }
      setCapturing(type)
      try {
        const r = await plantaService.captureSimulatedWeight(type)
        const w = r.weight.toString()
        if (type === 'Gross') setGrossInput(w)
        else setTareInput(w)
      } finally {
        setCapturing(null)
      }
    },
    [device],
  )

  const grossNum = parseFloat(grossInput.replace(',', '.')) || 0
  const tareNum = parseFloat(tareInput.replace(',', '.')) || 0
  const netCalc = grossNum > 0 && tareNum > 0 ? grossNum - tareNum : 0

  // El BRUTO se pesa al llegar (camión lleno); la TARA recién tras conciliar
  // (camión descargado). El backend bloquea la tara antes de Conciliado.
  const TARE_STATES = ['Conciliado', 'PesadoYMuestreado', 'EnAnalisis']
  const grossSaved = lote?.grossWeight != null
  const tareSaved = lote?.tare != null
  const tareAllowed = lote != null && TARE_STATES.includes(lote.shipmentState)

  // El neto a comparar usa el bruto ya guardado o el que se está por registrar.
  const grossForNet = grossSaved ? (lote?.grossWeight ?? 0) : grossNum
  const canSaveGross = lote != null && !grossSaved && grossNum > 0
  const canSaveTare =
    lote != null && !tareSaved && tareAllowed && tareNum > 0 && grossForNet > tareNum
  const canSave = canSaveGross || canSaveTare

  const handleGuardar = useCallback(async () => {
    if (!lote || !canSave) return
    setSaving(true)
    try {
      // Registramos lo que falte y sea válido en esta etapa. Cada peso es
      // write-once en el backend; el neto se calcula al llegar la tara.
      if (canSaveGross) {
        await plantaService.registerWeight(lote.id, 'Gross', grossNum, 'Iot')
      }
      if (canSaveTare) {
        await plantaService.registerWeight(lote.id, 'Tare', tareNum, 'Iot')
      }
      toaster.success({
        description: canSaveTare ? 'Pesaje neto guardado.' : 'Peso bruto guardado.',
      })
      // Re-leer
      const r = await plantaService.getBalanzaList({ search: lote.batchCode, pageSize: 1 })
      setLote(r.items[0] ?? null)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error guardando pesaje'
      toaster.error({ description: message })
    } finally {
      setSaving(false)
    }
  }, [lote, grossNum, tareNum, canSave, canSaveGross, canSaveTare])

  const deviceConnected = device?.status === 'Connected'

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title="Control de Pesaje (IoT)"
        subtitle="Balanza simulada — pesos bruto y tara"
        colors={colors}
      />

      {/* Buscador */}
      <Flex gap={2} alignItems="stretch">
        <Box flex="1" position="relative">
          <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="#b5aa9d">
            <LuSearch size={18} />
          </Box>
          <input
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleBuscar()
            }}
            placeholder="KTR-260422074118"
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              borderRadius: 6,
              border: '1px solid #e8dfd1',
              fontSize: 14,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          />
        </Box>
        <button
          type="button"
          onClick={handleBuscar}
          disabled={lookupLoading || !searchCode.trim()}
          style={{
            padding: '10px 20px',
            background: brand.primary,
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: lookupLoading ? 'not-allowed' : 'pointer',
            opacity: lookupLoading ? 0.5 : 1,
          }}
        >
          {lookupLoading ? 'Buscando...' : 'Buscar'}
        </button>
      </Flex>

      {lote && (
        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="#e8dfd1" p={6}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="lg"
              fontWeight="700"
              color={colors.text}
            >
              {lote.batchCode}
            </Text>
            <Flex alignItems="center" gap={2}>
              <Box
                color={deviceConnected ? '#2a8a87' : '#c8442f'}
                style={{ display: 'inline-flex' }}
              >
                <LuCircle size={10} fill="currentColor" />
              </Box>
              <Text
                fontSize="xs"
                fontWeight="700"
                color={deviceConnected ? '#1d6664' : '#9a3020'}
              >
                {device?.port ?? 'COM3'}: {deviceConnected ? 'Conectada' : 'Desconectada'}
              </Text>
              {device?.isSimulated && (
                <Box
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  fontSize="9px"
                  fontWeight="700"
                  bg="#f9eccf"
                  color="#92400E"
                >
                  SIMULADO
                </Box>
              )}
            </Flex>
          </Flex>

          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
            {/* Peso Bruto */}
            <Box bg="#faf6f0" borderRadius="lg" p={4}>
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="xs" fontWeight="700" color={colors.textMuted}>
                  1. Peso Bruto (Lleno)
                </Text>
                <button
                  type="button"
                  onClick={() => handleCapturar('Gross')}
                  disabled={
                    !deviceConnected || capturing != null || saving || lote.grossWeight != null
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    background: brand.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor:
                      !deviceConnected || lote.grossWeight != null
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: !deviceConnected || lote.grossWeight != null ? 0.5 : 1,
                  }}
                >
                  <LuDownload size={12} />
                  Capturar Peso
                </button>
              </Flex>
              <input
                type="number"
                step="0.001"
                value={grossInput}
                onChange={(e) => setGrossInput(e.target.value)}
                placeholder="0.000"
                disabled={lote.grossWeight != null || saving}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 6,
                  border: '1px solid #e8dfd1',
                  fontSize: 22,
                  fontWeight: 700,
                  textAlign: 'right',
                  background: 'white',
                }}
              />
              <Text fontSize="xs" color={colors.textMuted} textAlign="right" mt={1}>
                Tn
              </Text>
            </Box>

            {/* Tara */}
            <Box bg="#faf6f0" borderRadius="lg" p={4}>
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="xs" fontWeight="700" color={colors.textMuted}>
                  2. Tara (Vacío)
                </Text>
                <button
                  type="button"
                  onClick={() => handleCapturar('Tare')}
                  disabled={
                    !deviceConnected ||
                    capturing != null ||
                    saving ||
                    lote.tare != null ||
                    !tareAllowed
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    background: brand.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor:
                      !deviceConnected || lote.tare != null || !tareAllowed
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: !deviceConnected || lote.tare != null || !tareAllowed ? 0.5 : 1,
                  }}
                >
                  <LuDownload size={12} />
                  Capturar Peso
                </button>
              </Flex>
              <input
                type="number"
                step="0.001"
                value={tareInput}
                onChange={(e) => setTareInput(e.target.value)}
                placeholder="0.000"
                disabled={lote.tare != null || saving || !tareAllowed}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 6,
                  border: '1px solid #e8dfd1',
                  fontSize: 22,
                  fontWeight: 700,
                  textAlign: 'right',
                  background: tareAllowed ? 'white' : '#efe8dd',
                }}
              />
              <Text fontSize="xs" color={colors.textMuted} textAlign="right" mt={1}>
                {tareAllowed ? 'Tn' : 'Disponible tras conciliación'}
              </Text>
            </Box>

            {/* Peso Neto */}
            <Box
              bg={brand.primary}
              borderRadius="lg"
              p={4}
              color="white"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" opacity={0.85}>
                PESO NETO CALCULADO (TMH)
              </Text>
              <Text fontSize="3xl" fontWeight="800" mt={1}>
                {formatWeight(lote.netWeight ?? netCalc)}
              </Text>
              <Text fontSize="sm" opacity={0.85}>
                Tn
              </Text>
            </Box>
          </Grid>

          <Flex justifyContent="center" mt={6}>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={!canSave || saving}
              style={{
                padding: '12px 32px',
                background: brand.primary,
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                cursor: canSave && !saving ? 'pointer' : 'not-allowed',
                opacity: canSave && !saving ? 1 : 0.5,
              }}
            >
              {grossSaved && tareSaved
                ? 'Pesaje Oficial Completo'
                : saving
                  ? 'Guardando...'
                  : canSaveTare
                    ? 'Guardar Peso Neto'
                    : grossSaved && !tareAllowed
                      ? 'Bruto guardado · neto tras conciliar'
                      : 'Guardar Peso Bruto'}
            </button>
          </Flex>
        </Box>
      )}

      {!lote && (
        <Box bg="#faf6f0" borderRadius="lg" p={8} textAlign="center">
          <Text fontSize="sm" color={colors.textMuted}>
            Busque un lote por código (KTR-...) para iniciar el pesaje.
          </Text>
        </Box>
      )}
    </VStack>
  )
}
